<script>
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import Spinner from 'next/spinner/Spinner.vue';
import SearchedContacts from './SearchedContacts.vue';
import SelectedContacts from './SelectedContacts.vue';

export default {
  components: {
    Spinner,
    SearchedContacts,
    SelectedContacts,
  },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      search: '',
      searching: false,
      onSearch: debounce(event => {
        this.search = event.target.value;
        try {
          const attributeKeys = ['name', 'email', 'phone_number'];
          const payload = attributeKeys.map(attributeKey => {
            return {
              attribute_key: attributeKey,
              attribute_model: 'standard',
              custom_attribute_type: '',
              filter_operator: 'contains',
              query_operator: 'or',
              values: this.search,
            };
          });
          const queryPayload = filterQueryGenerator(payload);
          this.$store.dispatch('contacts/filter', { queryPayload, limit: 3 });
        } catch (error) {
          useAlert(
            this.$t('WGPT_BOARDS.ITEM.LIST.CARD.EDIT.SIDEBAR.API.ERROR_MESSAGE')
          );
        } finally {
          this.searching = false;
        }
      }, 750),
      updatedValue: this.modelValue,
    };
  },
  watch: {
    updatedValue(value) {
      this.$emit('update:modelValue', value);
    },
    modelValue(value, oldValue) {
      if (value.join() === oldValue.join()) return;
      this.updatedValue = value;
    },
  },
  methods: {
    addItem(contact) {
      this.updatedValue.push(contact.id);
    },
    onInput(event) {
      const value = event.target.value.trim();
      if (value === this.search || value === '') {
        this.searching = false;
        this.search = '';
        this.$store.dispatch('contacts/clearContactFilters');
        return;
      }

      if (value.length === 1) {
        this.search = value;
      }
      this.searching = true;
      this.onSearch(event);
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <label class="text-sm text-slate-600 dark:text-slate-400">{{
      $t('WGPT_BOARDS.FILTER.CONTACTS.TITLE')
    }}</label>

    <div class="relative">
      <Spinner v-if="searching" class="absolute size-3.5 top-2 left-3" />
      <span v-else class="absolute i-lucide-search size-3.5 top-2 left-3" />
      <input
        type="search"
        :placeholder="
          $t(
            'WGPT_BOARDS.ITEM.LIST.CARD.EDIT.CONTACTS.MULTI_SELECTOR.INPUT_PLACEHOLDER'
          )
        "
        class="reset-base outline-none w-full h-8 py-2 pl-9 pr-2 text-sm border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
        @input="onInput"
      />
    </div>

    <SearchedContacts
      v-show="search && !searching"
      :selected-ids="updatedValue"
      @add="addItem"
    />
    <SelectedContacts v-model="updatedValue" />
  </div>
</template>

<style lang="scss" scoped>
.wgpt-animate-pulse {
  animation: wgptAnimatePulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes wgptAnimatePulse {
  50% {
    opacity: 0.5;
  }
}

.badge {
  @apply inline-block bg-slate-50 dark:bg-woot-800 rounded-md text-slate-600 dark:text-slate-100 h-5 text-xxs font-semibold px-1 min-w-[16px] text-center;
}
</style>
